import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  Output,
  TemplateRef,
  ElementRef
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { HttpClient, HttpContext } from '@angular/common/http';
import { TranslatePipe } from '@ngx-translate/core';
import { Subject } from 'rxjs';
import { takeUntil, finalize } from 'rxjs/operators';
import {
  CompleteChecklistItemSection,
  CompleteChecklistSection,
  CompleteChecklistStatus,
  CompleteAttachment
} from '../models/complete-checklist';
import { UploaderConfig } from 'src/app/shared/ecpl-document-explorer/uploader-config';
import { EcplImageViewerModule } from 'src/app/shared/ecpl-image-viewer/ecpl-image-viewer.module';
import { SKIP_ERROR_TOAST } from 'src/app/shared/http-context-tokens';

@Component({
  selector: 'kuba-checklist-checkpoints',
  standalone: true,
  imports: [CommonModule, FormsModule, TranslatePipe, EcplImageViewerModule],
  templateUrl: './checklist-checkpoints.component.html',
  styleUrls: ['./checklist-checkpoints.component.css']
})
export class ChecklistCheckpointsComponent implements OnDestroy {
  @Input() sections: CompleteChecklistSection[] = [];
  @Input() checkpointExtra: TemplateRef<any> | null = null;
  @Input() canAnswer: (
    item: CompleteChecklistItemSection,
    status: CompleteChecklistStatus
  ) => boolean = () => true;
  @Input() scoreEnabled = false;
  @Input() uploader: UploaderConfig;
  @Input() references: { label: string; value: string }[] = [];
  @Output() statusChanged = new EventEmitter<{
    status: CompleteChecklistStatus;
    item: CompleteChecklistItemSection;
  }>();
  @Output() changed = new EventEmitter<CompleteChecklistItemSection>();
  @Output() uploadingChanged = new EventEmitter<boolean>();
  activeSection = 0;
  searchQuery = '';
  @Input() showRemaining = true;
  @Input() showSectionNavigation = true;
  filter: 'all' | 'unanswered' | 'deviations' = 'all';
  expanded = new Set<CompleteChecklistItemSection>();
  uploading = new Set<CompleteChecklistItemSection>();
  failedImages = new Set<string>();
  uploadError = false;
  preview: { path: string; name: string; type?: string } | null = null;
  private readonly destroy$ = new Subject<void>();

  constructor(
    private readonly http: HttpClient,
    private readonly host: ElementRef<HTMLElement>
  ) {}

  get items(): CompleteChecklistItemSection[] {
    return (this.sections ?? []).flatMap(section => section.Items ?? []);
  }
  get total(): number {
    return this.items.length;
  }
  get answered(): number {
    return this.items.filter(item => this.isAnswered(item)).length;
  }
  get deviations(): number {
    return this.items.filter(item => this.isDeviation(item)).length;
  }
  get longMode(): boolean {
    return this.total >= 25;
  }
  isAnswered(item: CompleteChecklistItemSection): boolean {
    return (item.Status ?? []).some(status => status.value);
  }
  isDeviation(item: CompleteChecklistItemSection): boolean {
    return (item.Status ?? []).some(
      status => status.value && this.statusKey(status) === 'DEVIATION'
    );
  }
  sectionAnswered(section: CompleteChecklistSection): number {
    return (section.Items ?? []).filter(item => this.isAnswered(item)).length;
  }
  visible(item: CompleteChecklistItemSection): boolean {
    const matchesSearch =
      !this.searchQuery.trim() ||
      (item.Title ?? '')
        .toLocaleLowerCase()
        .includes(this.searchQuery.trim().toLocaleLowerCase());
    return (
      matchesSearch &&
      (this.filter === 'all' ||
        (this.filter === 'unanswered'
          ? !this.isAnswered(item)
          : this.isDeviation(item)))
    );
  }
  sectionHasVisibleItems(section: CompleteChecklistSection): boolean {
    return (section.Items ?? []).some(item => this.visible(item));
  }
  sectionHasDeviation(section: CompleteChecklistSection): boolean {
    return (section.Items ?? []).some(item => this.isDeviation(item));
  }
  selectSection(index: number): void {
    this.activeSection = index;
    if (!this.longMode)
      this.host.nativeElement
        .querySelector(`[data-section="${index}"]`)
        ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }
  statusKey(status: CompleteChecklistStatus): string {
    return (
      status.TranslationKey ||
      (status.Label === 'Ok'
        ? 'OK'
        : status.Label === 'Not Applicable'
          ? 'NOT_APPLICABLE'
          : 'DEVIATION')
    );
  }
  answer(
    item: CompleteChecklistItemSection,
    status: CompleteChecklistStatus
  ): void {
    if (!this.canAnswer(item, status)) return;
    this.statusChanged.emit({ status, item });
    if (this.statusKey(status) === 'DEVIATION') this.expanded.add(item);
    this.changed.emit(item);
  }
  markRestNa(section: CompleteChecklistSection): void {
    for (const item of section.Items ?? []) {
      if (this.isAnswered(item)) continue;
      const status = item.Status?.find(
        answer => this.statusKey(answer) === 'NOT_APPLICABLE'
      );
      if (status) this.answer(item, status);
    }
  }
  toggle(item: CompleteChecklistItemSection): void {
    if (this.expanded.has(item)) this.expanded.delete(item);
    else this.expanded.add(item);
  }
  firstUnanswered(): void {
    const section = (this.sections ?? []).findIndex(s =>
      (s.Items ?? []).some(item => !this.isAnswered(item))
    );
    if (section < 0) return;
    this.searchQuery = '';
    this.filter = 'unanswered';
    this.activeSection = section;
    requestAnimationFrame(() => {
      const first =
        this.host.nativeElement.querySelector<HTMLElement>('.checkpoint');
      first?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      first
        ?.querySelector<HTMLButtonElement>('.checkpoint-answers button')
        ?.focus({ preventScroll: true });
    });
  }
  isImage(file: CompleteAttachment): boolean {
    return (
      file.mimetype?.startsWith('image/') ||
      /\.(png|jpe?g|webp|heic)(\?|$)/i.test(file.path ?? '')
    );
  }
  openImage(path: string, name: string, type?: string): void {
    this.preview = { path, name, type };
  }
  remove(item: CompleteChecklistItemSection, file: CompleteAttachment): void {
    item.Attachments = (item.Attachments ?? []).filter(
      attachment => attachment !== file
    );
    this.changed.emit(item);
  }
  upload(event: Event, item: CompleteChecklistItemSection): void {
    const input = event.target as HTMLInputElement;
    const files = Array.from(input.files ?? []);
    input.value = '';
    if (
      !this.uploader?.uploaderUri ||
      this.uploading.has(item) ||
      files.length === 0
    )
      return;
    this.uploadError = false;
    this.uploading.add(item);
    this.uploadingChanged.emit(true);
    let pending = files.length;
    for (const file of files) {
      const form = new FormData();
      form.append('file', file);
      for (const [key, value] of Object.entries(
        this.uploader.addtionalData ?? {}
      ))
        form.append(key, String(value));
      this.http
        .post<CompleteAttachment>(this.uploader.uploaderUri, form, {
          context: new HttpContext().set(SKIP_ERROR_TOAST, true)
        })
        .pipe(
          takeUntil(this.destroy$),
          finalize(() => {
            if (--pending === 0) {
              this.uploading.delete(item);
              this.uploadingChanged.emit(this.uploading.size > 0);
            }
          })
        )
        .subscribe({
          next: result => {
            if (!result?.path) {
              this.uploadError = true;
              return;
            }
            item.Attachments ??= [];
            item.Attachments.push(result);
            this.changed.emit(item);
          },
          error: () => {
            this.uploadError = true;
          }
        });
    }
  }
  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
